import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import { UnitTextField } from '@/common/components/UnitTextField'
import { useBankInstitutions } from '@/common/hooks/queries/useBankInstitutions'
import { useBillingCaptureMethods } from '@/common/hooks/queries/useBillingCaptureMethods'
import useDebounce from '@/common/hooks/useDebounce'
import { BankInstitutionsTransformer } from '@/common/services/bankInstitutionsTransformer'
import { BillingCaptureMethodsTransformer } from '@/common/services/billingCaptureMethodsTransformer'
import { Mapper } from '@/common/services/mapper'
import { EstablishmentCompanyData } from '@/common/types/establishment/company.type'
import { formatCurrency, formatPercentage } from '@/common/utils/formatters'
import { updatePoliticsFinancialSchema, UpdatePoliticsFinancialValidationSchema } from '@/features/establishments/schemas/politics/updateFinancial.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useUpdateEstablishmentFinancialPolitics } from '../hooks/useUpdateEstablishmentFinancialPolitics'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useQueryClient } from '@tanstack/react-query'

interface UpdateFinancialModalProps {
  establishmentCompany?: EstablishmentCompanyData;
  isFetching?: boolean;
}

export const UpdateFinancialModal = ({ establishmentCompany, isFetching }: UpdateFinancialModalProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchBankInstitutions, setSearchBankInstitutions] = useDebounce('')
  const [searchBillingCaptureMethods, setSearchBillingCaptureMethods] = useDebounce('')

  const { data: bankInstitutionOptions } = useBankInstitutions({
    filters: { search: searchBankInstitutions },
    mapper: Mapper.mapWithTransformer({ transformer: BankInstitutionsTransformer.optionsTransformer() }),
  })

  const { data: billingCaptureMethods } = useBillingCaptureMethods({
    filters: { search: searchBillingCaptureMethods },
    mapper: Mapper.mapWithTransformer({ transformer: BillingCaptureMethodsTransformer.optionsTransformer() }),
  })

  const formMethods = useForm<UpdatePoliticsFinancialValidationSchema>({
    resolver: zodResolver(updatePoliticsFinancialSchema),
  })

  const {
    control,
    handleSubmit,
    register,
    reset,
    setError,
    formState: { errors },
  } = formMethods

  const {
    mutate: updateFinancialPolitics,
    isPending: isUpdateFinancialPoliticsPending
  } = useUpdateEstablishmentFinancialPolitics({
    establishmentCompany: establishmentCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompany?.id] })
      setIsOpen(false)
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<UpdatePoliticsFinancialValidationSchema> = (form) => updateFinancialPolitics(form);

  useEffect(() => {
    if (isOpen) {
      reset({
        payment_advance_daily_interest: establishmentCompany?.matrix_branch_payment_advance_daily_interest
          ? formatPercentage(Number(establishmentCompany?.matrix_branch_payment_advance_daily_interest))
          : '',
        payment_advance_min_total_to_be_paid: establishmentCompany?.matrix_branch_payment_advance_min_total_to_be_paid
          ? formatCurrency(Number(establishmentCompany?.matrix_branch_payment_advance_min_total_to_be_paid))
          : '',
        capture_methods: BillingCaptureMethodsTransformer.optionsTransformer()(
          establishmentCompany?.matrix_branch?.capture_methods?.map(({ capture_method }) => capture_method) || []
        ),
        bank_account_information: {
          account: establishmentCompany?.matrix_branch?.bank_account_information?.account || '',
          agency: establishmentCompany?.matrix_branch?.bank_account_information?.agency || '',
          bank_institution: establishmentCompany?.matrix_branch?.bank_account_information?.bank_institution
            ? BankInstitutionsTransformer.optionsTransformer()(
              [establishmentCompany?.matrix_branch?.bank_account_information?.bank_institution]
            )[0]
            : null as any,
          payee_name: establishmentCompany?.matrix_branch?.bank_account_information?.payee_name || '',
        },
      })
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_fee_details_modal"
          operationKlassToCheckPermission="Admin::Establishment::CompanyBranches::UpdateFinancialInformation"
          className="h-[2.125rem] text-sm text-white-800"
          disabled={!!isFetching}
        >
          Editar
        </Button>
      }
      title="Editando Financeiro"
      dataTestid="update_financial__establishment_politics"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700 rounded-br-lg"
      cancelButtonClassName="bg-white-100 rounded-bl-lg"
      disabled={isUpdateFinancialPoliticsPending}
      confirmButtonIsLoading={isUpdateFinancialPoliticsPending}
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <Controller
              control={control}
              name='payment_advance_daily_interest'
              disabled={isUpdateFinancialPoliticsPending}
              render={({ field }) => (
                <UnitTextField
                  label="Taxa diária de juros de adiantamento"
                  decimals={2}
                  unit="%"
                  {...field}
                  errorMessage={errors?.payment_advance_daily_interest?.message || undefined}
                />
              )}
            />

            <SelectController
              control={control}
              name="capture_methods"
              label="Meios de captura"
              isMulti
              errorMessage={errors?.capture_methods?.message}
              onInputChange={setSearchBillingCaptureMethods}
              options={billingCaptureMethods}
              isDisabled={isUpdateFinancialPoliticsPending}
            />

            <Box className="flex flex-col gap-4 p-5">
              <p className='text-[16px]'>
                Detalhes bancários
              </p>

              <SelectController
                label="Banco"
                name="bank_account_information.bank_institution"
                control={control}
                options={bankInstitutionOptions}
                onInputChange={(value) => setSearchBankInstitutions(value)}
                errorMessage={errors?.bank_account_information?.bank_institution?.message}
                isDisabled={isUpdateFinancialPoliticsPending}
              />

              <div className="flex gap-2">
                <TextField
                  label="Agência (com dígito)"
                  className="grow"
                  {...register('bank_account_information.agency')}
                  errorMessage={errors?.bank_account_information?.agency?.message || undefined}
                  disabled={isUpdateFinancialPoliticsPending}
                />

                <TextField
                  label="Conta (com dígito)"
                  className="grow"
                  {...register('bank_account_information.account')}
                  errorMessage={errors?.bank_account_information?.account?.message || undefined}
                  disabled={isUpdateFinancialPoliticsPending}
                />
              </div>

              <TextField
                label="Favorecido"
                {...register('bank_account_information.payee_name')}
                errorMessage={errors?.bank_account_information?.payee_name?.message || undefined}
                disabled={isUpdateFinancialPoliticsPending}
              />
            </Box>

            <Controller
              control={control}
              name='payment_advance_min_total_to_be_paid'
              disabled={isUpdateFinancialPoliticsPending}
              render={({ field }) => (
                <CurrencyTextField
                  label="Valor mínimo para adiantamento"
                  {...field}
                  errorMessage={errors?.payment_advance_min_total_to_be_paid?.message || undefined}
                />
              )}
            />
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
